import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import {
  notificationsFetchAPI,
  markAllNotificationsReadAPI,
} from "api/notifications/notificationsApiHelper"
import Notify from "components/Common/notify"

// Pull the items array + total pages out of the API response, tolerating a
// couple of common envelope shapes.
const extractItems = body => {
  if (!body) return []
  if (Array.isArray(body)) return body
  if (Array.isArray(body.items)) return body.items
  if (Array.isArray(body.data)) return body.data
  if (body.data && Array.isArray(body.data.items)) return body.data.items
  return []
}
const extractTotalPages = body => {
  if (!body) return 0
  return body.totalPages || body.data?.totalPages || 0
}

export const NotificationsFetch = createAsyncThunk(
  "Notifications/NotificationsFetch",
  async data => {
    const response = await notificationsFetchAPI(data)
    return {
      data: response?.data,
      status: response?.status,
    }
  },
)

export const MarkAllNotificationsRead = createAsyncThunk(
  "Notifications/MarkAllNotificationsRead",
  async (_, { rejectWithValue }) => {
    try {
      const response = await markAllNotificationsReadAPI()
      return { status: response?.status }
    } catch (error) {
      Notify("error", "Failed to mark notifications as read")
      return rejectWithValue(error?.message)
    }
  },
)

const initialState = {
  notifications: [],
  totalPages: 0,
  currentPage: 1,
  pageSize: 5,
  loading: false,
  markAllLoading: false,
  error: null,
}

export const NotificationSlice = createSlice({
  name: "NotificationSlice",
  initialState,
  reducers: {
    // A live notification pushed via SignalR. It is newer than everything in
    // the list, so it goes on top; while viewing the first page we keep a fixed
    // window of `pageSize` items by dropping the oldest one from the bottom.
    AddLiveNotification: (state, action) => {
      if (state.currentPage !== 1) return
      state.notifications = [action.payload, ...state.notifications].slice(
        0,
        state.pageSize,
      )
    },
    ClearNotifications: state => {
      state.notifications = []
    },
  },
  extraReducers(builder) {
    builder
      .addCase(NotificationsFetch.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(NotificationsFetch.fulfilled, (state, action) => {
        state.loading = false
        const body = action.payload?.data
        console.log("notifications fetch payload ->", body)
        state.notifications = extractItems(body)
        state.totalPages = extractTotalPages(body)
        state.currentPage = action.meta?.arg?.pageNumber || 1
        state.pageSize = action.meta?.arg?.pageSize || state.pageSize
      })
      .addCase(NotificationsFetch.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
    builder
      .addCase(MarkAllNotificationsRead.pending, state => {
        state.markAllLoading = true
      })
      .addCase(MarkAllNotificationsRead.fulfilled, state => {
        state.markAllLoading = false
        // Reflect the change locally: mark every shown notification as read.
        state.notifications = state.notifications.map(n =>
          n && typeof n === "object" ? { ...n, isRead: true } : n,
        )
      })
      .addCase(MarkAllNotificationsRead.rejected, state => {
        state.markAllLoading = false
      })
  },
})

export const { AddLiveNotification, ClearNotifications } =
  NotificationSlice.actions

export const allNotifications = state => state.allNotifications

export default NotificationSlice.reducer
